import React, { useCallback, useEffect, useState } from 'react';
import useApi from '../hooks/useApi';
import { ManagedUser, Role } from '../types';
import UserTable from '../components/UserTable/UserTable';
import InviteUserModal from '../components/InviteUserModal/InviteUserModal';
import EditRolesModal from '../components/EditRolesModal/EditRolesModal';
import EditUserModal from '../components/EditUserModal/EditUserModal';
import ConfirmBlockModal from '../components/ConfirmBlockModal/ConfirmBlockModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal/ConfirmDeleteModal';
import Loading from '../components/Loading/Loading';
import { useDebounce } from 'use-debounce';

const PER_PAGE = 20;

const UserManagementPage: React.FC = () => {
  const { securedRequest, loading } = useApi();

  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageError, setPageError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<ManagedUser | null>(null);
  const [userToEditDetails, setUserToEditDetails] = useState<ManagedUser | null>(null);
  const [userToDelete, setUserToDelete] = useState<ManagedUser | null>(null);
  const [userToToggleBlock, setUserToToggleBlock] = useState<ManagedUser | null>(null);

  const fetchRoles = useCallback(async (signal?: AbortSignal) => {
    const { data, error } = await securedRequest('get', '/api/admin/roles', null, signal);
    if (data) {
      setRoles(data);
    }
    if (error && !signal?.aborted) {
      console.error('Failed to fetch roles:', error);
    }
  }, [securedRequest]);

  const fetchUsers = useCallback(async (targetPage: number, query: string, signal?: AbortSignal) => {
    setPageError(null);
    const url = `/api/admin/users?page=${targetPage}&perPage=${PER_PAGE}&q=${encodeURIComponent(query)}`;
    const { data, error } = await securedRequest('get', url, null, signal);
    if (data) {
      setUsers(data.users ?? []);
      setTotal(data.total ?? 0);
      setPage(data.page ?? 0);
    }
    if (error && !signal?.aborted) {
      setPageError('Failed to load users.');
    }
  }, [securedRequest]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      await Promise.all([
        fetchUsers(0, debouncedSearchQuery, controller.signal),
        fetchRoles(controller.signal)
      ]);
    };
    fetchData();
    return () => controller.abort();
  }, [fetchUsers, fetchRoles, debouncedSearchQuery]);

  const handleUserInvited = (newUser: ManagedUser) => {
    if (!users) return;
    setUsers(prev => [newUser, ...(prev || [])]);
    setTotal(prev => prev + 1);
  };

  const handleUserUpdated = (updatedUser: ManagedUser) => {
    if (!users) return;
    setUsers(prev =>
      (prev || []).map(u => u.userId === updatedUser.userId ? updatedUser : u)
    );
  };

  const handleToggleBlock = async (user: ManagedUser) => {
    const action = user.blocked ? 'unblock' : 'block';

    const { data, error } = await securedRequest(
      'patch',
      `/api/admin/users/${encodeURIComponent(user.userId)}`,
      { blocked: !user.blocked }
    );

    if (error) {
      alert(`Failed to ${action} user.`);
      return;
    }

    setUsers(prev =>
      (prev || []).map(u => u.userId === user.userId ? { ...u, blocked: data.blocked } : u)
    );
    setUserToToggleBlock(null);
  };

  const handleDeleteUser = async (user: ManagedUser) => {
    const { error } = await securedRequest(
      'delete',
      `/api/admin/users/${encodeURIComponent(user.userId)}`
    );

    if (error) {
      alert('Failed to delete user.');
      return;
    }

    setUsers(prev => (prev || []).filter(u => u.userId !== user.userId));
    setTotal(prev => prev - 1);
    setUserToDelete(null);
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  if (users === null || roles === null) {
    return <Loading />;
  }

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Manage Users</h2>
          <p className="text-muted mb-0">
            {total} user{total !== 1 ? 's' : ''} total
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
          + Invite User
        </button>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {pageError && <div className="alert alert-danger">{pageError}</div>}

      {showInviteModal && (
        <InviteUserModal
          roles={roles}
          onClose={() => setShowInviteModal(false)}
          onUserInvited={user => {
            handleUserInvited(user);
            setShowInviteModal(false);
          }}
        />
      )}

      {userToEditDetails && (
        <EditUserModal
          user={userToEditDetails}
          onClose={() => setUserToEditDetails(null)}
          onUserUpdated={(updatedUser) => {
            handleUserUpdated(updatedUser);
            setUserToEditDetails(null);
          }}
        />
      )}

      {userToEdit && (
        <EditRolesModal
          user={userToEdit}
          roles={roles}
          onClose={() => setUserToEdit(null)}
          onRolesUpdated={handleUserUpdated}
        />
      )}

      {userToToggleBlock && (
        <ConfirmBlockModal
          user={userToToggleBlock}
          loading={loading}
          onConfirm={() => handleToggleBlock(userToToggleBlock)}
          onClose={() => setUserToToggleBlock(null)}
        />
      )}

      {userToDelete && (
        <ConfirmDeleteModal
          user={userToDelete}
          loading={loading}
          onConfirm={() => handleDeleteUser(userToDelete)}
          onClose={() => setUserToDelete(null)}
        />
      )}

      <UserTable
        users={users}
        onEditUser={setUserToEditDetails}
        onEditRoles={setUserToEdit}
        onToggleBlock={setUserToToggleBlock}
        onDelete={setUserToDelete}
      />

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Page {page + 1} of {totalPages}
          </small>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={page === 0 || loading}
              onClick={() => fetchUsers(page - 1, debouncedSearchQuery)}
            >
              Previous
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={page >= totalPages - 1 || loading}
              onClick={() => fetchUsers(page + 1, debouncedSearchQuery)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
